import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  PeopleAlt as PeopleIcon,
  Event as CalendarIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { Event, EVENT_ZONES } from '../../types/Event';
import { useAuth } from '../../contexts/AuthContext';
import { MEMBER_TYPES } from '../../types/User';
import EventCountdown from '../../components/EventCountdown';
import ResponsiveTitle from '../../components/ResponsiveTitle';

interface NextEventCardProps {
  event: Event;
  user: any; // Prop passed from Home
  onRegister: () => void;
  onParticipantsClick: (e: React.MouseEvent) => void;
  additionalEventsCount?: number;
  onSplashClick: () => void;
  customStatusText?: string;
  customStatusColor?: string;
  onLocationClick: (locationId: string) => void;
}

const NextEventCard: React.FC<NextEventCardProps> = ({
  event,
  user,
  onRegister,
  onParticipantsClick,
  additionalEventsCount = 0,
  onSplashClick,
  customStatusText,
  customStatusColor,
  onLocationClick
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [homeAddressDialogOpen, setHomeAddressDialogOpen] = useState(false);

  // --- Logic ---
  const participants = event.participants || [];
  const isRegistered = user && participants.includes(user.uid);
  const isFull = participants.length >= event.maxParticipants;

  const bgImage = event.zone === EVENT_ZONES.WAN
    ? '/images/event-types/WAN_Photo.jpg'
    : event.zone === EVENT_ZONES.COMMUNAL
      ? '/images/event-types/LAN_Photo.jpg'
      : '/images/event-types/Domicile_Photo.jpg';

  // Helper pour sécuriser la conversion de date
  const toDate = (d: any): Date => {
    if (!d) return new Date(); // Fallback si null/undefined

    // Si c'est un Timestamp Firestore (objet avec seconds)
    if (typeof d === 'object' && d.seconds) {
      return new Date(d.seconds * 1000);
    }

    // Tentative de parsing standard
    const date = new Date(d);

    // Si invalide, on retourne une date sûre (aujourd'hui) pour éviter le crash toISOString
    if (isNaN(date.getTime())) {
      console.error("Date invalide reçue dans NextEventCard:", d);
      return new Date(); // Fallback
    }

    return date;
  };

  const startDate = toDate(event.startDate);
  const endDate = toDate(event.endDate);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLocationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.zone === EVENT_ZONES.WAN) {
      if (event.location) window.open(event.location.startsWith('http') ? event.location : `https://${event.location}`, '_blank');
    } else if (event.zone === EVENT_ZONES.COMMUNAL) {
      if (event.locationId) {
        onLocationClick(event.locationId);
      } else if (event.googleMapsUrl) {
        window.open(event.googleMapsUrl, '_blank');
      } else if (event.location) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`, '_blank');
      }
    } else if (event.zone === EVENT_ZONES.HOME) {
      setHomeAddressDialogOpen(true);
    }
  };

  return (
    <>
      <Card
        elevation={4}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6), // Semi-transparent background
          backdropFilter: 'blur(10px)', // Frosted glass effect
          display: 'flex',
          flexDirection: 'column', // Structure principale verticale pour accueillir la barre
          width: '100%',
          minHeight: 220, // Identique à UpcomingEventCard
          borderRadius: 4,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[12]
          }
        }}
        onClick={onRegister}
      >
        {/* --- BARRE DE STATUT --- */}
        <Box sx={{
          bgcolor: customStatusColor ? customStatusColor : ((user && isRegistered) ? theme.palette.success.main : theme.palette.error.main),
          color: 'white',
          textAlign: 'center',
          py: 0.5,
          fontWeight: 'bold',
          letterSpacing: 1,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          transition: 'all 0.3s ease'
        }}>
          {customStatusText ? customStatusText : (
            user ? (
              isRegistered ? "Inscrit" : (isFull && event.zone !== EVENT_ZONES.WAN ? "Complet" : "Non inscrit - Cliquez pour vous inscrire")
            ) : (
              "Pour s'inscrire, connectez-vous"
            )
          )}
        </Box>

        {/* --- WRAPPER HORIZONTAL (Image + Contenu) --- */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1
        }}>
          {/* --- IMAGE SECTION (Gauche) --- */}
          <Box sx={{
            position: 'relative',
            width: { xs: '100%', md: '35%' },
            minWidth: { md: 300 },
            height: { xs: 200, md: 'auto' }
          }}>
            <Box
              component="img"
              src={bgImage}
              alt="Event cover"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
              }}
            />

            {/* Overlay sombre léger */}
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.2)' }} />

            {/* Badge ZONE */}
            <Box sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: 'rgba(0,0,0,0.8)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              backdropFilter: 'blur(4px)',
            }}>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold', letterSpacing: 1 }}>
                {event.zone === EVENT_ZONES.WAN ? 'WAN' : event.zone === EVENT_ZONES.COMMUNAL ? 'LAN' : 'HOME'}
              </Typography>
            </Box>

            {/* Countdown Overlay (Bas de l'image) */}
            <Box sx={{
              position: 'absolute',
              bottom: 12,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Box sx={{ transform: 'scale(0.85)' }}>
                {/* Utilisation de toISOString pour s'assurer que le countdown reçoit une string valide */}
                <EventCountdown targetDate={startDate.toISOString()} textColor="#fff" variant="large" />
              </Box>
            </Box>
          </Box>

          {/* --- CONTENT SECTION (Droite) --- */}
          <Box sx={{
            flex: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
          }}>

            <Box>
              {/* Header: Titre */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>
                    PROCHAIN ÉVÉNEMENT
                  </Typography>
                  <ResponsiveTitle variant="h5" component="h2" minFontSize={14} maxFontSize={24} style={{
                    fontWeight: 800,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                    sx={{ mt: 1 }}>
                    {event.title}
                  </ResponsiveTitle>
                </Box>
              </Box>

              {/* Infos Détaillées */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>

                {/* Dates */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CalendarIcon color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(startDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Jusqu'au {formatDate(endDate)}
                    </Typography>
                  </Box>
                </Box>

                {/* Lieu */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {event.zone === EVENT_ZONES.WAN ? <LinkIcon color="action" /> : <LocationIcon color="action" />}
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    onClick={handleLocationClick}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '4px',
                      '&:hover': { color: theme.palette.primary.main }
                    }}
                  >
                    {event.zone === EVENT_ZONES.WAN ? 'Discord (Rejoindre)' : (event.location || 'Voir sur la carte')}
                  </Typography>
                </Box>

                {/* Le Matos - Uniquement pour les événements physiques */}
                {event.zone !== EVENT_ZONES.WAN && (
                  <Box sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.info.main}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CampaignIcon fontSize="small" color="info" />
                      <ComputerIcon fontSize="small" color="info" />
                      <Typography variant="subtitle2" color="info.main" fontWeight="bold">
                        Le Matos
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                      On gère l'infrastructure réseau, tu apportes ta machine.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Footer: Places & Participants */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: theme.palette.action.hover,
                  px: 2,
                  py: 0.8,
                  borderRadius: 2
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onParticipantsClick(e);
                }}
              >
                <PeopleIcon color={isFull && event.zone !== EVENT_ZONES.WAN ? "error" : "primary"} fontSize="small" />
                <Typography variant="body2" fontWeight="bold">
                  {event.zone === EVENT_ZONES.WAN
                    ? `${participants.length} participant(s)`
                    : `${participants.length} / ${event.maxParticipants} inscrits`
                  }
                </Typography>
              </Box>

            </Box>

            {/* SPLASH ADDITIONAL EVENTS */}
            {additionalEventsCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onSplashClick();
                }}
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  transform: 'rotate(-5deg)',
                  zIndex: 10,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'rotate(0deg) scale(1.1)',
                  }
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    py: 1,
                    px: 3,
                    borderRadius: '20px 4px 20px 4px',
                    boxShadow: theme.shadows[8],
                    border: '2px solid white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    animation: 'pulse 2s infinite ease-in-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                      '100%': { transform: 'scale(1)' },
                    }
                  }}
                >
                  <Typography variant="h6" component="div" sx={{ fontWeight: 900, lineHeight: 1, fontSize: '1.2rem' }}>
                    + {additionalEventsCount}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                    {additionalEventsCount > 1 ? 'Événements' : 'Événement'}
                  </Typography>
                </Box>
              </Box>
            )}

          </Box>

        </Box>
      </Card>

      <Dialog
        open={homeAddressDialogOpen}
        onClose={() => setHomeAddressDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Adresse de l'événement</DialogTitle>
        <DialogContent>
          <Typography>
            Cet événement a lieu au domicile d'un membre.
            Pour obtenir l'adresse précise, veuillez contacter directement un administrateur ou l'organisateur.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHomeAddressDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(NextEventCard);
